import type {
  EEGSelectionBandPower,
  EEGSelectionSpectrogramCell,
  EEGSelectionSpectrumBin,
} from "./eegSegmentSelectionTypes";
import {
  computeFFT,
  computeSpectrogram as computeSelectionSpectrogram,
  welchMethod,
  calculateRelativeBandPowers,
} from "../utils/fftUtils";
import { clampNumber } from "./eegViewerDisplayHelpers";

export function normalizeWindowedSamples(samples: number[], maxSamples = 512) {
  const finiteSamples = samples.filter((value) => Number.isFinite(value));
  if (finiteSamples.length <= maxSamples) return finiteSamples;
  const stride = finiteSamples.length / maxSamples;
  const reduced: number[] = [];
  for (let index = 0; index < maxSamples; index += 1) {
    reduced.push(finiteSamples[Math.min(finiteSamples.length - 1, Math.floor(index * stride))]);
  }
  return reduced;
}

export function buildSelectionSpectrum(
  samples: number[],
  sampleRate: number,
  maxFrequency = 70,
): EEGSelectionSpectrumBin[] {
  if (samples.length < 8 || !Number.isFinite(sampleRate) || sampleRate <= 0) {
    return [];
  }

  const windowed = normalizeWindowedSamples(samples, 2048);
  if (windowed.length < 8) return [];
  const mean = windowed.reduce((sum, value) => sum + value, 0) / windowed.length;
  const centered = windowed.map((value) => value - mean);
  const fft = computeFFT(centered, sampleRate, "hanning");
  const bins = fft.frequencies
    .map((hz, index) => ({ hz, power: fft.power[index] || 0 }))
    .filter((bin) => bin.hz > 0 && bin.hz <= maxFrequency)
    .slice(0, 48);
  const peak = Math.max(...bins.map((bin) => bin.power), 0);
  if (peak <= 0) return bins.map((bin) => ({ ...bin, power: 0 }));
  return bins.map((bin) => ({ ...bin, power: bin.power / peak }));
}

export function estimateSelectionDominantFrequency(samples: number[], sampleRate: number) {
  const spectrum = buildSelectionSpectrum(samples, sampleRate);
  if (spectrum.length === 0) return null;
  const dominant = spectrum.reduce((best, candidate) =>
    candidate.power > best.power ? candidate : best,
  );
  return dominant.power > 0 ? dominant.hz : null;
}

export function buildSelectionSpectrogram(
  samples: number[],
  sampleRate: number,
): EEGSelectionSpectrogramCell[] {
  if (samples.length < 16 || !Number.isFinite(sampleRate) || sampleRate <= 0) {
    return [];
  }

  const windowed = normalizeWindowedSamples(samples, 4096);
  const windowSize = Math.max(32, Math.min(256, Math.floor(windowed.length / 2)));
  const hopSize = Math.max(16, Math.floor(windowSize / 2));
  const spectrogram = computeSelectionSpectrogram(
    windowed,
    sampleRate,
    windowSize,
    hopSize,
    "hanning",
  );
  const minMagnitude = Math.min(...spectrogram.magnitudes.flat(), 0);
  const maxMagnitude = Math.max(...spectrogram.magnitudes.flat(), minMagnitude + 1);
  const range = Math.max(1, maxMagnitude - minMagnitude);
  const cells: EEGSelectionSpectrogramCell[] = [];
  spectrogram.times.slice(0, 12).forEach((timeOffsetSeconds, frameIndex) => {
    const magnitudes = spectrogram.magnitudes[frameIndex] || [];
    const bands = [
      { hz: 3, min: 0.5, max: 4 },
      { hz: 7, min: 4, max: 8 },
      { hz: 11, min: 8, max: 13 },
      { hz: 22, min: 13, max: 30 },
      { hz: 38, min: 30, max: 45 },
    ];
    bands.forEach((band) => {
      const bandMagnitudes = spectrogram.frequencies
        .map((hz, index) =>
          hz >= band.min && hz < band.max ? magnitudes[index] : null,
        )
        .filter((value): value is number => typeof value === "number");
      const power =
        bandMagnitudes.length > 0
          ? (bandMagnitudes.reduce((sum, value) => sum + value, 0) /
              bandMagnitudes.length -
              minMagnitude) /
            range
          : 0;
      cells.push({
        timeOffsetSeconds,
        hz: band.hz,
        power: clampNumber(power, 0, 1),
      });
    });
  });
  return cells;
}

/**
 * Relative band powers from Welch PSD (S2). Replaces the prior heuristic
 * that invented weights from dominant-frequency distance.
 */
export function buildSelectionBandPower(
  samples: number[] | null | undefined,
  sampleRate?: number,
): EEGSelectionBandPower {
  const empty = {
    delta: null,
    theta: null,
    alpha: null,
    beta: null,
    gamma: null,
  };
  if (
    !samples?.length ||
    !sampleRate ||
    !Number.isFinite(sampleRate) ||
    sampleRate <= 0
  ) {
    return empty;
  }

  try {
    const windowed = normalizeWindowedSamples(samples, 4096);
    const psd = welchMethod(windowed, sampleRate);
    const relative = calculateRelativeBandPowers(psd);
    // Store as 0–1 fractions for selection UI / report helpers.
    return {
      delta: relative.delta != null ? relative.delta / 100 : null,
      theta: relative.theta != null ? relative.theta / 100 : null,
      alpha: relative.alpha != null ? relative.alpha / 100 : null,
      beta: relative.beta != null ? relative.beta / 100 : null,
      gamma: relative.gamma != null ? relative.gamma / 100 : null,
    };
  } catch {
    return empty;
  }
}

export function formatNullableNumber(
  value: number | null | undefined,
  digits = 1,
  suffix = "",
) {
  return typeof value === "number" && Number.isFinite(value)
    ? `${value.toFixed(digits)}${suffix}`
    : "n/a";
}
